import api from './api'

export interface LoginCredentials {
  email: string
  password: string
}

export interface LoginResponse {
  access_token: string
  user: {
    _id: string
    nom: string
    prenom: string
    email: string
    role: string
    userType: string
    permissions: string[]
    organizationId?: string
  }
}

export interface RegisterData {
  nom: string
  prenom: string
  email: string
  password: string
  userType: string
  role: string
  departement?: string
  telephone?: string
  organizationId?: string
}

class AuthService {
  // Connexion
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post('/auth/login', credentials)
    
    // Gérer la nouvelle structure de réponse
    if (response.data.success && response.data.data) {
      const { token, refreshToken, user } = response.data.data
      
      // Stocker le token
      localStorage.setItem('auth_token', token)
      localStorage.setItem('user', JSON.stringify(user))
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken)
      }
      
      return {
        access_token: token,
        user: user
      }
    } else {
      throw new Error(response.data.message || 'Erreur de connexion')
    }
  }

  // Déconnexion
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.warn('Erreur lors de la déconnexion:', error)
    } finally {
      // Nettoyer le stockage local
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
    }
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token')
    return !!token
  }

  // Obtenir l'utilisateur connecté
  getCurrentUser(): any {
    const userStr = localStorage.getItem('user')
    if (!userStr) return null
    try {
      return JSON.parse(userStr)
    } catch (error) {
      console.warn('Erreur lors du parsing de l\'utilisateur:', error)
      localStorage.removeItem('user')
      return null
    }
  }

  // Obtenir le token
  getToken(): string | null {
    const token = localStorage.getItem('auth_token')
    return token || null
  }

  // Obtenir le refresh token
  getRefreshToken(): string | null {
    const refreshToken = localStorage.getItem('refresh_token')
    return refreshToken || null
  }

  // Rafraîchir le token
  async refreshToken(): Promise<string> {
    const response = await api.post('/auth/refresh')
    const { access_token } = response.data
    localStorage.setItem('auth_token', access_token)
    return access_token
  }

  // Changer le mot de passe
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', {
      currentPassword,
      newPassword
    })
  }

  // Obtenir le profil utilisateur
  async getProfile(): Promise<any> {
    const response = await api.get('/auth/profile')
    return response.data
  }

  // Mettre à jour le profil
  async updateProfile(profileData: any): Promise<any> {
    const response = await api.patch('/auth/profile', profileData)
    const user = response.data
    localStorage.setItem('user', JSON.stringify(user))
    return user
  }

  // Vérifier les permissions
  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser()
    if (!user || !user.permissions) return false
    return user.permissions.includes(permission)
  }

  // Vérifier le rôle
  hasRole(role: string): boolean {
    const user = this.getCurrentUser()
    if (!user) return false
    return user.role === role
  }

  // Vérifier le type d'utilisateur
  isUserType(userType: string): boolean {
    const user = this.getCurrentUser()
    if (!user) return false
    return user.userType === userType
  }

  // Demander la récupération de mot de passe
  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email })
  }

  // Réinitialiser le mot de passe
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post('/auth/reset-password', { 
      token, 
      newPassword 
    })
  }

  // Vérifier la validité d'un token de réinitialisation
  async verifyResetToken(token: string): Promise<boolean> {
    try {
      await api.get(`/auth/verify-reset-token/${token}`)
      return true
    } catch (error) {
      console.error('Erreur lors de la vérification du token de réinitialisation:', error)
      return false
    }
  }
}

export const authService = new AuthService()
